import React, { useEffect, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CircularProgress, FormControlLabel, Switch,
  TextField, Typography,
} from '@mui/material';
import { attendanceApi, AttendanceSettings } from '../../services/attendanceApi';

interface Props {
  studioId: string;
  onChanged?: (s: AttendanceSettings) => void;
}

/**
 * Super-admin switch for staff attendance at one studio, plus the geofence
 * radius and the grace period for late / early.
 */
const AttendanceSettingsCard: React.FC<Props> = ({ studioId, onChanged }) => {
  const [settings, setSettings] = useState<AttendanceSettings | null>(null);
  const [radius, setRadius] = useState('150');
  const [grace, setGrace] = useState('15');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    let live = true;
    setSettings(null);
    attendanceApi
      .getSettings(studioId)
      .then((s) => {
        if (!live) return;
        setSettings(s);
        setRadius(String(s.radiusMeters));
        setGrace(String(s.graceMinutes));
      })
      .catch((e) => live && setError(e?.message || 'Could not load settings'));
    return () => {
      live = false;
    };
  }, [studioId]);

  const save = async (patch: Partial<AttendanceSettings>) => {
    setSaving(true);
    setError(null);
    setSaved(false);
    try {
      const s = await attendanceApi.updateSettings(studioId, patch);
      setSettings(s);
      setSaved(true);
      onChanged?.(s);
    } catch (e: any) {
      setError(e?.message || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const r = Number(radius);
  const g = Number(grace);
  const valid = Number.isInteger(r) && r >= 50 && r <= 1000 && Number.isInteger(g) && g >= 0 && g <= 60;

  return (
    <Card sx={{ mb: 3, borderRadius: 2 }}>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={700}>Attendance settings</Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          When on, instructors and managers of this studio see Punch in / Punch out in the partner app.
          They can only punch while within the radius of the studio's saved location.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {settings?.enabled && settings.locationSet === false && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This studio has no location saved, so nobody can punch in. Set the studio's address on the map first.
          </Alert>
        )}
        {!settings ? (
          <CircularProgress size={20} />
        ) : (
          <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
            <FormControlLabel
              control={
                <Switch
                  checked={settings.enabled}
                  disabled={saving}
                  onChange={(e) => save({ enabled: e.target.checked })}
                />
              }
              label={settings.enabled ? 'On' : 'Off'}
            />
            <TextField
              size="small" type="number" label="Radius (metres)" value={radius}
              onChange={(e) => setRadius(e.target.value)} sx={{ width: 150 }}
              helperText="50–1000"
            />
            <TextField
              size="small" type="number" label="Grace (minutes)" value={grace}
              onChange={(e) => setGrace(e.target.value)} sx={{ width: 150 }}
              helperText="On time within this"
            />
            <Button
              variant="outlined" disabled={saving || !valid}
              onClick={() => save({ radiusMeters: r, graceMinutes: g })}
            >
              Save
            </Button>
            {saved && <Typography variant="caption" color="success.main">Saved</Typography>}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default AttendanceSettingsCard;
